import React, { useState, useEffect } from 'react';
import { questions, getNextQuestion, calculateRecommendation } from '../data/questions';
import Question from './Question';
import Results from './Results';
import ResultsGate from './ResultsGate';
import './DecisionTree.css';

// n8n Webhook URL - replace with your actual webhook URL
const WEBHOOK_URL = 'https://YOUR-N8N-WEBHOOK-URL-HERE/webhook';

const DecisionTree = () => {
  const [currentQuestionId, setCurrentQuestionId] = useState('q1');
  const [answers, setAnswers] = useState({});
  const [history, setHistory] = useState(['q1']);
  const [showResults, setShowResults] = useState(false);
  const [showGate, setShowGate] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [userData, setUserData] = useState(null);

  // Check if user has already submitted in last 3 days
  useEffect(() => {
    const stored = localStorage.getItem('quiz_completed');
    if (stored) {
      try {
        const data = JSON.parse(stored);
        const threeDaysAgo = Date.now() - (3 * 24 * 60 * 60 * 1000);
        if (data.timestamp > threeDaysAgo) {
          setUserData(data);
        } else {
          localStorage.removeItem('quiz_completed');
        }
      } catch (e) {
        localStorage.removeItem('quiz_completed');
      }
    }
  }, []);

  const currentQuestion = questions.find(q => q.id === currentQuestionId);
  const progress = (Object.keys(answers).length / 9) * 100; // 6 universal + up to 3 technical

  const handleAnswer = (questionId, answer) => {
    setIsTransitioning(true);
    
    // Store the answer
    const newAnswers = { ...answers, [questionId]: answer };
    setAnswers(newAnswers);

    // Track answer with PostHog
    if (window.posthog) {
      window.posthog.capture('question_answered', {
        question_id: questionId,
        answer: Array.isArray(answer) ? answer.join(', ') : answer,
        question_number: history.length
      });
    }

    setTimeout(() => {
      // Determine next question based on branching logic
      const nextQuestionId = getNextQuestion(questionId, answer, newAnswers);
      
      if (nextQuestionId === 'RESULTS') {
        // Check if user already submitted
        if (userData) {
          setShowResults(true);
        } else {
          setShowGate(true);
        }
      } else {
        setCurrentQuestionId(nextQuestionId);
        setHistory([...history, nextQuestionId]);
      }
      
      setIsTransitioning(false);
    }, 600);
  };

  const handleGateSubmit = async (formData) => {
    const recommendation = calculateRecommendation(answers);
    
    // Store in localStorage (expires in 3 days)
    const completionData = {
      ...formData,
      timestamp: Date.now()
    };
    localStorage.setItem('quiz_completed', JSON.stringify(completionData));
    setUserData(completionData);

    // Track with PostHog
    if (window.posthog) {
      window.posthog.capture('quiz_completed', {
        strategy: recommendation.strategy,
        innovation_type: answers['q1'],
        stage: answers['q2'],
        funding: answers['q3'],
        budget: answers['q14'],
        disclosure_status: answers['q16'],
        name: formData.name,
        company: formData.company,
        email: formData.email
      });

      // Identify user
      window.posthog.identify(formData.email, {
        email: formData.email,
        name: formData.name,
        company: formData.company
      });
    }

    // Send to n8n webhook
    try {
      if (WEBHOOK_URL && !WEBHOOK_URL.includes('YOUR-N8N')) {
        await fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            event: 'quiz_completed',
            timestamp: new Date().toISOString(),
            user: {
              name: formData.name,
              email: formData.email,
              company: formData.company
            },
            result: {
              strategy: recommendation.strategy,
              title: recommendation.title
            },
            answers: answers
          })
        });
      }
    } catch (error) {
      console.error('Webhook error:', error);
      // Continue anyway - don't block user
    }

    // Show results
    setShowGate(false);
    setShowResults(true);
  };

  const handleBack = () => {
    if (history.length > 1) {
      setIsTransitioning(true);
      
      setTimeout(() => {
        const newHistory = [...history];
        newHistory.pop();
        const previousQuestionId = newHistory[newHistory.length - 1];
        
        // Remove the answer for the current question
        const newAnswers = { ...answers };
        delete newAnswers[currentQuestionId];
        
        setAnswers(newAnswers);
        setHistory(newHistory);
        setCurrentQuestionId(previousQuestionId);
        setIsTransitioning(false);
      }, 300);
    }
  };

  if (showGate) {
    const recommendation = calculateRecommendation(answers);
    
    // Calculate confidence based on score differences
    let confidence = 1;
    // Simple confidence calculation - you can make this more sophisticated
    confidence = Math.abs(5); // Placeholder - will be more dynamic
    
    return (
      <ResultsGate
        strategy={recommendation.strategy}
        confidence={confidence}
        onSubmit={handleGateSubmit}
      />
    );
  }

  if (showResults) {
    const recommendation = calculateRecommendation(answers);
    return (
      <Results 
        recommendation={recommendation}
        answers={answers}
        onRestart={() => {
          setCurrentQuestionId('q1');
          setAnswers({});
          setHistory(['q1']);
          setShowResults(false);
        }}
      />
    );
  }

  return (
    <div className="decision-tree">
      <div className="progress-container">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="tree-container">
        <div className="question-container">
          <Question
            question={currentQuestion}
            onAnswer={(answer) => handleAnswer(currentQuestion.id, answer)}
            isTransitioning={isTransitioning}
            questionNumber={history.length}
            onBack={handleBack}
            showBack={history.length > 1}
            backDisabled={isTransitioning}
          />
        </div>
      </div>
    </div>
  );
};

export default DecisionTree;
